/**
 * The API contract shared by server and client: response envelope, the closed
 * ErrorCode union (spec §4.10) and the DTOs the foundation owns.
 *
 * Features add their own DTOs in their own directories; they must not widen
 * `ERROR_CODES` (the union is closed and the client switches on it).
 */

import type { UserPrefs } from "./userPrefs.js";

export type Lang = "sv" | "en";
export type Role = "student" | "teacher";

/** Every error the API may return. Closed union — see spec §4.10. */
export const ERROR_CODES = [
  "VALIDATION",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "CSRF",
  "NOT_FOUND",
  "EXAM_ACTIVE",
  "EXAM_EXPIRED",
  "EXAM_SUBMITTED",
  "EMAIL_TAKEN",
  "EXAM_BANK_INSUFFICIENT",
  "PAYLOAD_TOO_LARGE",
  "RATE_LIMITED",
  "INTERNAL",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** Default HTTP status for each error code. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  VALIDATION: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  CSRF: 403,
  NOT_FOUND: 404,
  EXAM_ACTIVE: 409,
  EXAM_EXPIRED: 409,
  EXAM_SUBMITTED: 409,
  EMAIL_TAKEN: 409,
  EXAM_BANK_INSUFFICIENT: 409,
  PAYLOAD_TOO_LARGE: 413,
  RATE_LIMITED: 429,
  INTERNAL: 500,
};

export interface ApiErrorBody {
  code: ErrorCode;
  message: string;
  /** field path → message, filled for VALIDATION. */
  fields?: Record<string, string>;
  requestId: string;
}

export type ApiSuccess<T> = { ok: true; data: T };
export type ApiFailure = { ok: false; error: ApiErrorBody };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export function isApiSuccess<T>(r: ApiResponse<T>): r is ApiSuccess<T> {
  return r.ok;
}

/* ------------------------------- DTOs -------------------------------- */

/** The full user row as the owner sees it (`GET /api/auth/me`). */
export interface User {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  language: Lang;
  newCardsPerDay: number;
  /** Hand-in bonus points: 0 | 1 | 1.5 | 2. */
  bonusPoints: number;
  /** The "Kom igång" answers; null until the card was answered or skipped (slice C). */
  prefs: UserPrefs | null;
  createdAt: number;
  lastActiveAt: number;
}

/** What request handlers get from `c.get('user')` and what `/me` returns. */
export type SessionUser = User;

export interface HealthResponse {
  ok: true;
  version: string;
  now: number;
  timezone: string;
}
